import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const NDA_CLAUSES = [
  "Les informations détaillées (chiffres, identité, documents, vidéos) relatives aux entreprises à céder sont strictement confidentielles.",
  "Le signataire s'engage à n'utiliser ces informations que dans le cadre de l'étude d'une éventuelle reprise, et à ne les divulguer à aucun tiers sans autorisation écrite de PROAC Transmission.",
  "Le signataire s'interdit tout démarchage direct du cédant, de ses salariés, clients ou fournisseurs en dehors du cadre organisé par PROAC Transmission.",
  "Cet engagement de confidentialité reste valable pendant toute la durée des échanges et deux (2) ans après leur terme.",
];

export default function NdaDialog({ open, onOpenChange, onSigned }) {
  const { user, checkAuth } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [accepted, setAccepted] = useState(false);
  const [signing, setSigning] = useState(false);

  const sign = async () => {
    if (!accepted) { toast.error("Veuillez cocher la case d'acceptation."); return; }
    if (!name.trim()) { toast.error("Veuillez saisir votre nom pour signer."); return; }
    setSigning(true);
    try {
      await api.post("/nda/sign", { signed_name: name.trim(), accepted });
      await checkAuth();
      toast.success("Accord de confidentialité signé. Les dossiers complets sont maintenant débloqués.");
      onOpenChange(false);
      onSigned?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSigning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="nda-dialog">
        <DialogHeader>
          <div className="w-12 h-12 bg-[#1B2A4A] flex items-center justify-center mb-2"><ShieldCheck className="w-6 h-6 text-[#E0C173]" strokeWidth={1.5} /></div>
          <DialogTitle className="font-display text-2xl text-[#1B2A4A]">Accord de confidentialité</DialogTitle>
          <DialogDescription className="text-[#475569]">
            Pour accéder aux dossiers complets des entreprises à céder, vous devez signer l'engagement de confidentialité ci-dessous. Cette signature est unique et débloque l'ensemble des dossiers.
          </DialogDescription>
        </DialogHeader>

        <ol className="space-y-3 my-2 text-sm text-[#475569] list-decimal list-inside bg-[#F8F7F4] border border-gray-200 p-5">
          {NDA_CLAUSES.map((c, i) => <li key={i} className="leading-relaxed">{c}</li>)}
        </ol>

        <div className="flex items-start gap-3 mt-1">
          <Checkbox id="nda-accept" checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} data-testid="nda-accept-checkbox" className="mt-0.5" />
          <Label htmlFor="nda-accept" className="text-sm text-[#1B2A4A] leading-relaxed cursor-pointer">
            J'ai lu et j'accepte les termes de cet accord de confidentialité.
          </Label>
        </div>

        <div className="mt-4">
          <Label className="text-xs uppercase tracking-wider text-[#475569]">Signature — saisissez votre nom complet</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Prénom Nom" className="rounded-none mt-1.5 border-gray-300" data-testid="nda-name-input" />
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)} data-testid="nda-cancel-btn">Annuler</Button>
          <Button onClick={sign} disabled={signing} className="rounded-none bg-[#1B2A4A] hover:bg-[#0D1629]" data-testid="nda-sign-btn">
            {signing ? "Signature…" : "Signer et accéder aux dossiers"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
